import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

import AppDialog from "components/common/AppDialog";
import { useSavePdfJobMutation, type PdfJobDetail } from "features/pdfTools/pdfTools";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { apiErrorCode, apiErrorText, normalizeSplitFolderName, splitSavePreview } from "./splitJobUi";

export interface SaveSplitResultDialogProps {
  open: boolean;
  onClose: () => void;
  job: PdfJobDetail;
  vendor: string;
}

/**
 * Save puts the reviewed result where download bots put documents and upload
 * jobs read them: the client's vendor data folder. Only the folder name is
 * asked for; the vendor part of the path is what tells an upload job whose SSN
 * file knows these employees.
 */
export default function SaveSplitResultDialog({
  open,
  onClose,
  job,
  vendor,
}: SaveSplitResultDialogProps) {
  const dispatch = useAppDispatch();
  const [savePdfJob, { isLoading: saving }] = useSavePdfJobMutation();
  const [raw, setRaw] = useState(job.save.default_folder_name);
  const [touched, setTouched] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRaw(job.save.default_folder_name);
    setTouched(false);
    setConflict(null);
    setError(null);
  }, [open, job.job_id, job.save.default_folder_name]);

  const { name, error: nameError } = normalizeSplitFolderName(raw);
  const preview = splitSavePreview(job.save.data_root, name);
  const subfolders = job.save.result_subfolders;

  const submit = async (overwrite: boolean) => {
    if (nameError) {
      setTouched(true);
      return;
    }
    setError(null);
    try {
      await savePdfJob({ jobId: job.job_id, folder_name: name, overwrite }).unwrap();
      dispatch(
        pushNotification({
          message: "Saving started - the files are being copied to Data Storage.",
          severity: "success",
        }),
      );
      onClose();
    } catch (err: unknown) {
      if (apiErrorCode(err) === "destination_not_empty" && !overwrite) {
        setConflict(apiErrorText(err, "The folder already holds files."));
        return;
      }
      setError(apiErrorText(err, "Could not start saving. Please try again."));
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Save to Data Storage"
      maxWidth="sm"
      showCloseIcon={!saving}
      actions={
        conflict ? (
          <>
            <Button onClick={() => setConflict(null)} disabled={saving}>
              Back
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => void submit(true)}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Overwrite
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void submit(false)}
              disabled={saving || !!nameError}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              Save
            </Button>
          </>
        )
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          One folder per employee goes into the client&apos;s <b>{vendor}</b> data folder,
          the place an upload job picks its Source folder from. The error pages stay in the
          job&apos;s workspace.
        </Typography>
        <TextField
          autoFocus
          size="small"
          label="Folder name *"
          value={raw}
          disabled={saving || !!conflict}
          onChange={(e) => {
            setRaw(e.target.value);
            setTouched(true);
            setConflict(null);
          }}
          error={touched && !!nameError}
          helperText={
            touched && nameError
              ? nameError
              : "Letters, digits, spaces and _ . ( ) - ; no slashes."
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !conflict) void submit(false);
          }}
        />
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            fontFamily: "monospace",
            fontSize: 13,
            overflowWrap: "anywhere",
          }}
        >
          {preview}
        </Box>
        {subfolders.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            This splitter sorts the forms into {subfolders.join(" and ")} folders: an upload
            job takes one of them as its Source folder.
          </Typography>
        )}
        {/* The server's sentence names the folder and what Overwrite does. */}
        {conflict && <Alert severity="warning">{conflict}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    </AppDialog>
  );
}
